/**
 * 端内（App WebView）环境判定与参数读取。
 *
 * 协议以 App 侧《成绩页 H5 ↔ 原生 桥接文档 v1.0.0》为准：
 * 原生在 atDocumentStart 注入 window.WHParams（参数）与 window.WHBridge（能力），
 * 两者首屏同步可读，不需要等事件、不需要轮询。
 *
 * 判定只看 WHBridge 是否存在（与文档 §6.3 一致）。不再依赖 URL 参数 ——
 * 参数走注入对象，凭据不会进 nginx / CDN 日志，也不会被 GA 当 page_location 上报。
 */

// 与 App 文档 §3.2 对齐：H5 结果页只服务新 HSK
export const MOCK_TYPE_NEW_HSK = 4

// 与 App 文档 §3.1 对齐。当前只做全真模拟，其余留作可读性
export const EXAMINATION_TYPE = {
  PRACTICE: 1,
  MOCK: 2,
  SPECIAL: 3,
  ASSIGNMENT: 4,
}

/**
 * 开发调试用的假注入：让浏览器也能走端内分支。
 *
 * 端内逻辑没法靠"console 里注入再刷新"验证 —— 判定在首帧同步求值，刷新就丢。
 * 所以给一个 URL 开关：?_mockbridge=1，仅非 production 生效。
 */
function devMockParams() {
  if (process.env.NODE_ENV === 'production') return null
  try {
    if (new URLSearchParams(window.location.search).get('_mockbridge') !== '1') return null
  } catch (e) {
    return null
  }
  return {
    WHToken: '',
    WHLanguage: 'zh-Hans',
    WHAcceptLanguage: 'zh-cn',
    WHIsRTL: false,
    WHSafeAreaTop: 62,
    WHSafeAreaBottom: 34,
    WHPlatform: 'iOS',
    WHAppVersion: '0.0.0-dev',
    WHSystemVersion: '0.0',
    WHExaminationId: '',
    WHPaperId: '',
    WHExaminationType: EXAMINATION_TYPE.MOCK,
    WHMockType: MOCK_TYPE_NEW_HSK,
    __mock: true,
  }
}

let mockParamsCache

function rawParams() {
  if (typeof window === 'undefined') return null
  if (window.WHParams) return window.WHParams
  if (mockParamsCache === undefined) mockParamsCache = devMockParams()
  return mockParamsCache
}

/** 端内 = 原生注入了 WHBridge（文档 §6.3） */
export function isEmbed() {
  if (typeof window === 'undefined') return false
  if (typeof window.WHBridge !== 'undefined') return true
  // 调试模式：有假参数就当端内，bridge 调用由 bridge.js 里的 mock 分支兜住
  return !!(rawParams() && rawParams().__mock)
}

/**
 * 读取原生下发的参数，并归一化成页面用的形态。
 *
 * 归一化在这里做一次，页面里不再直接碰 WH* 字段 —— 将来协议改名只动这一处。
 */
export function getEmbedParams() {
  const p = rawParams() || {}
  return {
    token: p.WHToken || '',
    // 服务端语言口径，用于 Accept-Language 请求头（文档 §2.1，别和 WHLanguage 混用）
    acceptLanguage: p.WHAcceptLanguage || '',
    // App 的 .lproj 目录名，用于前端资源与文案匹配
    language: p.WHLanguage || '',
    isRTL: !!p.WHIsRTL,
    safeAreaTop: Number(p.WHSafeAreaTop) || 0,
    safeAreaBottom: Number(p.WHSafeAreaBottom) || 0,
    platform: p.WHPlatform || '',
    appVersion: p.WHAppVersion || '',
    systemVersion: p.WHSystemVersion || '',
    // ⚠️ 服务端是 int64，必须按字符串处理（文档 §3）。用 Number 会丢精度，
    // 表现是"报告查不到"这类查无此记录的假象。
    examinationId: p.WHExaminationId == null ? '' : String(p.WHExaminationId),
    paperId: p.WHPaperId == null ? '' : String(p.WHPaperId),
    examinationType: Number(p.WHExaminationType) || 0,
    mockType: Number(p.WHMockType) || 0,
    isMock: !!p.__mock,
  }
}

/**
 * 本期只做全真模拟（examinationType=2）。
 *
 * App 文档 §3.1 说 H5 还可能收到练习模式(1)与作业(4)，但那两种报告语义不同：
 * 作业没有「再考一遍」与付费诊断；练习模式按 §3.3 连 examinationId 都是空串、
 * 报告接口根本调不了。产品已确认本期只做 2，其余场景 App 侧继续用原生结果页。
 */
export function isSupportedScene() {
  const { examinationType } = getEmbedParams()
  return examinationType === EXAMINATION_TYPE.MOCK
}
